import { useCallback, useRef } from 'react';

import { useRouter } from 'expo-router';
import { useLoyaltyOffersInCart } from 'features/menu/pages/menu-object-page/use-loyalty-offers-in-cart';

import { ISection } from '@rbi-ctg/menu';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { isLoyaltyOffer } from 'state/loyalty/types';
import { IMenuObject } from 'state/menu/types';
import { isCombo, isPicker } from 'utils/menu';
import { routes } from 'utils/routing';

export const useHandleMenuItemInOffer = (setIsOffer: (isOffer: boolean) => void) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const selectedOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const { getLoyaltyOfferInCart } = useLoyaltyOffersInCart();

  const menuObjectIdRef = useRef<string | undefined>(undefined);

  return useCallback(
    async (menuItem: NonNullable<IMenuObject> | Partial<ISection>) => {
      const alreadyChecked = !!menuItem._id && menuObjectIdRef.current === menuItem._id;
      if (alreadyChecked) {
        return;
      }
      menuObjectIdRef.current = menuItem._id;

      const isOfferBenefit = Boolean(
        (isCombo(menuItem) || isPicker(menuItem)) && menuItem?.isOfferBenefit
      );
      setIsOffer(isOfferBenefit);

      const isSelectedOfferBenefit = () => {
        let result = false;
        const menuItemId = menuItem._id;

        if (isLoyaltyOffer(selectedOffer)) {
          result = Boolean(
            selectedOffer?.incentives?.some(
              incentive => incentive && '_id' in incentive && incentive?._id === menuItemId
            )
          );
        }

        return result;
      };

      // This will prevent the case of a user going back to the product wizard for an offer item
      // without having the offer context causing price to show 0 and the item not adding to cart
      if (isOfferBenefit && (!selectedOffer || (selectedOffer && !isSelectedOfferBenefit()))) {
        // Try to find the offer in the cart or redirect to Offer List page
        const offerInCart = getLoyaltyOfferInCart();
        if (offerInCart) {
          dispatch(actions.loyalty.setSelectedOffer(offerInCart));
        } else if (menuItem) {
          // hack related to above comment.
          // VERY SPECIFICALLY: if you deep link (only deep link!) to an offer AND no store is selected and
          // you select a store the PDP (here) doesn't know about it until you refresh the reward detail page behind
          // it so dismiss to get there and then you need to come back from reward detail to PDP (here) again
          router.dismiss();
        } else {
          // what was happening before (if needed)
          router.replace(routes.offers);
        }
      }
    },
    [dispatch, getLoyaltyOfferInCart, router, selectedOffer, setIsOffer]
  );
};
